import type { ComponentType, PropsWithChildren } from 'react'
import { createContext, useContext, useMemo } from 'react'

import type { UseQueryResult } from 'react-query'

interface BaseQueryContext<T, Success extends boolean> {
  _isSuccess: Success
  _isFetching: boolean
  _isLoading: boolean
  _isError: boolean
  _queryError: unknown
  orElse: (value: T) => T
  refetch: UseQueryResult<T>['refetch']
}

type UnsuccessfulQueryContext<T> = Partial<T> & BaseQueryContext<T, false>

type SuccessfulQueryContext<T> = T & BaseQueryContext<T, true>

type QueryContext<T> = UnsuccessfulQueryContext<T> | SuccessfulQueryContext<T>

type HookName<D extends string> = `use${Capitalize<D>}`
type ProviderName<D extends string> = `${Capitalize<D>}Provider`
type ProviderType = ComponentType<PropsWithChildren>
type HookType<T> = () => QueryContext<T>

type ResultFieldName<D extends string> = HookName<D> | ProviderName<D>

export type QueryContextElements<T, D extends string> = {
  [key in ResultFieldName<D>]: key extends ProviderName<D>
    ? ProviderType
    : HookType<T>
}

function capitalize(name: string) {
  return name.charAt(0).toUpperCase() + name.slice(1)
}

export function createQueryProvider<T, DisplayName extends string>(
  name: DisplayName,
  useQuery: () => UseQueryResult<T>,
): QueryContextElements<T, DisplayName> {
  const Context = createContext<QueryContext<T> | undefined>(undefined)

  const providerName = `${capitalize(name)}Provider`

  const Provider = ({ children }: PropsWithChildren) => {
    const {
      data,
      isSuccess,
      isIdle,
      isFetching,
      isLoading,
      isError,
      error,
      refetch,
    } = useQuery()

    const context = useMemo<QueryContext<T>>(
      () =>
        Object.assign([], data ?? {}, {
          _isSuccess: isSuccess,
          _isFetching: isIdle || isFetching,
          _isLoading: isLoading,
          _isError: isError,
          _queryError: error,
          orElse: (value: T) => (data ?? value) as T,
          refetch,
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [data, isIdle, isFetching, isSuccess, isError, error, refetch],
    )

    return <Context.Provider value={context}>{children}</Context.Provider>
  }

  Provider.displayName = providerName

  const useQueryName = `use${capitalize(name)}`

  function useQueryContext() {
    const context = useContext(Context)

    if (context === undefined) {
      throw new Error(`${useQueryName} must be used within a ${providerName}`)
    }

    return context
  }

  useQueryContext.displayName = useQueryName

  return {
    [providerName]: Provider,
    [useQueryName]: useQueryContext,
  } as unknown as QueryContextElements<T, DisplayName>
}
